import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import "./font.css";

const SelebrityManager = () => {
  const [celebrities, setCelebrities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingDesktop, setUploadingDesktop] = useState(false);
  const [uploadingMobile, setUploadingMobile] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [showForm, setShowForm] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [updatingOrder, setUpdatingOrder] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [activeBrightnessType, setActiveBrightnessType] = useState('desktop');
  
  const desktopFileInputRef = useRef(null);
  const mobileFileInputRef = useRef(null);
  
  // Form data
  const [formData, setFormData] = useState({
    fullname: '',
    russian_name: '',
    image: '',
    mobile_image: '',
    mobile_background_position: '50%',
    desktop_brightness: '100',
    mobile_brightness: '100'
  });
  
  const [fileNames, setFileNames] = useState({
    desktop: '',
    mobile: ''
  });
  
  // Get image URL
  const getImageUrl = (path) => {
    if (!path) return null;
    let cleanPath = path;
    if (cleanPath.startsWith('selebrity-image/')) {
      cleanPath = cleanPath.replace('selebrity-image/', '');
    }
    const { data } = supabase.storage.from('selebrity-image').getPublicUrl(cleanPath);
    return data.publicUrl;
  };
  
  // Load celebrities
  useEffect(() => {
    loadCelebrities();
  }, []);
  
  const loadCelebrities = async () => {
    try {
      setLoading(true);
      setError('');
      const { data, error } = await supabase
        .from('selebrity-slider')
        .select('*')
        .order('id', { ascending: true });
      if (error) throw error;
      setCelebrities(data || []);
      setCurrentPage(1);
    } catch (err) {
      console.error('Loading error:', err);
      setError(`Error loading celebrities: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Swap IDs for ordering
  const swapCelebrityIds = async (celebrityA, celebrityB) => {
    try {
      await supabase.from('selebrity-slider').update({ id: -celebrityA.id, updated_at: new Date().toISOString() }).eq('id', celebrityA.id);
      await supabase.from('selebrity-slider').update({ id: celebrityA.id, updated_at: new Date().toISOString() }).eq('id', celebrityB.id);
      await supabase.from('selebrity-slider').update({ id: celebrityB.id, updated_at: new Date().toISOString() }).eq('id', -celebrityA.id);
    } catch (err) {
      console.error('Error swapping IDs:', err);
      throw err;
    }
  };
  
  const moveCelebrityUp = async (index) => {
    if (index === 0 || updatingOrder) return;
    try {
      setUpdatingOrder(true);
      const celebrityA = celebrities[index];
      const celebrityB = celebrities[index - 1];
      await swapCelebrityIds(celebrityA, celebrityB);
      const reorderedCelebrities = [...celebrities];
      reorderedCelebrities[index] = celebrityB;
      reorderedCelebrities[index - 1] = celebrityA;
      const tempId = celebrityA.id;
      celebrityA.id = celebrityB.id;
      celebrityB.id = tempId;
      setCelebrities(reorderedCelebrities);
      setSuccess('Order changed successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error changing order');
      await loadCelebrities(); 
    } finally {
      setUpdatingOrder(false);
    }
  };
  
  const moveCelebrityDown = async (index) => {
    if (index === celebrities.length - 1 || updatingOrder) return;
    try {
      setUpdatingOrder(true);
      const celebrityA = celebrities[index];
      const celebrityB = celebrities[index + 1];
      await swapCelebrityIds(celebrityA, celebrityB);
      const reorderedCelebrities = [...celebrities];
      reorderedCelebrities[index] = celebrityB;
      reorderedCelebrities[index + 1] = celebrityA;
      const tempId = celebrityA.id;
      celebrityA.id = celebrityB.id;
      celebrityB.id = tempId;
      setCelebrities(reorderedCelebrities);
      setSuccess('Order changed successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error changing order');
      await loadCelebrities();
    } finally {
      setUpdatingOrder(false);
    }
  };
  
  // Upload image
  const uploadImageToSupabase = async (file, folder = 'desktop') => {
    try {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
      if (!validTypes.includes(file.type.toLowerCase())) {
        throw new Error('Only images are supported (JPEG, PNG, WEBP, GIF)');
      }
     const maxSize = 50 * 1024 * 1024; // 50MB
if (file.size > maxSize) {
  throw new Error(`File size should not exceed 50MB. Your file: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
}
      const originalFileName = file.name;
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `${folder}/${fileName}`;
      const { data, error: uploadError } = await supabase.storage
        .from('selebrity-image')
        .upload(filePath, file, { cacheControl: '3600', upsert: false, contentType: file.type });
      if (uploadError) throw uploadError;
      const relativePath = `selebrity-image/${filePath}`;
      return { url: relativePath, fileName: originalFileName };
    } catch (err) {
      console.error('Image upload error:', err);
      throw err;
    }
  };
  
  const handleDesktopImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploadingDesktop(true);
      setError('');
      const result = await uploadImageToSupabase(file, 'desktop');
      setFormData(prev => ({ ...prev, image: result.url }));
      setFileNames(prev => ({ ...prev, desktop: result.fileName }));
    } catch (err) {
      setError(`Image upload error: ${err.message}`);
    } finally {
      setUploadingDesktop(false);
      if (desktopFileInputRef.current) desktopFileInputRef.current.value = '';
    }
  };
  
  const handleMobileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploadingMobile(true);
      setError('');
      const result = await uploadImageToSupabase(file, 'mobile');
      setFormData(prev => ({ ...prev, mobile_image: result.url }));
      setFileNames(prev => ({ ...prev, mobile: result.fileName }));
    } catch (err) {
      setError(`Image upload error: ${err.message}`);
    } finally {
      setUploadingMobile(false);
      if (mobileFileInputRef.current) mobileFileInputRef.current.value = '';
    }
  };
  
  const deleteImageFromStorage = async (imagePath) => {
    if (!imagePath) return;
    try {
      let cleanPath = imagePath;
      if (cleanPath.startsWith('selebrity-image/')) {
        cleanPath = cleanPath.replace('selebrity-image/', '');
      }
      await supabase.storage.from('selebrity-image').remove([cleanPath]);
    } catch (err) {
      console.error('Error deleting image:', err);
    }
  };
  
  const handleRemoveImage = (imageType) => {
    setFormData(prev => ({
      ...prev,
      [imageType === 'desktop' ? 'image' : 'mobile_image']: ''
    }));
    setFileNames(prev => ({ ...prev, [imageType]: '' }));
    if (imageType === 'desktop' && desktopFileInputRef.current) {
      desktopFileInputRef.current.value = '';
    } else if (mobileFileInputRef.current) {
      mobileFileInputRef.current.value = '';
    }
  };
  
  const handleSave = async () => {
    if (!formData.fullname.trim()) {
      setError('Full name is required');
      return;
    }
    if (!formData.image) {
      setError('Desktop image is required');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const celebrityData = {
        fullname: formData.fullname.trim(),
        russian_name: formData.russian_name.trim(),
        image: formData.image,
        mobile_image: formData.mobile_image || null,
        mobile_background_position: formData.mobile_background_position || '50%',
        desktop_brightness: formData.desktop_brightness || '100',
        mobile_brightness: formData.mobile_brightness || '100',
        updated_at: new Date().toISOString()
      };
      if (editingId) {
        const { error } = await supabase.from('selebrity-slider').update(celebrityData).eq('id', editingId);
        if (error) throw error;
        setSuccess('Celebrity updated successfully!');
      } else {
        celebrityData.created_at = new Date().toISOString();
        const { error } = await supabase.from('selebrity-slider').insert([celebrityData]);
        if (error) throw error;
        setSuccess('Celebrity added successfully!');
      }
      await loadCelebrities();
      resetForm();
      setShowForm(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleEdit = (celebrity) => {
    setEditingId(celebrity.id);
    setFormData({
      fullname: celebrity.fullname || '',
      russian_name: celebrity.russian_name || '',
      image: celebrity.image || '',
      mobile_image: celebrity.mobile_image || '',
      mobile_background_position: celebrity.mobile_background_position || '50%',
      desktop_brightness: celebrity.desktop_brightness || '100',
      mobile_brightness: celebrity.mobile_brightness || '100'
    });
    setFileNames({
      desktop: celebrity.image ? 'Uploaded' : '',
      mobile: celebrity.mobile_image ? 'Uploaded' : ''
    });
    setShowForm(true);
  };
  
  const handleDelete = async (id) => {
    try {
      setDeleting(true);
      const celebrityToDelete = celebrities.find(c => c.id === id);
      if (!celebrityToDelete) throw new Error('Celebrity not found');
      if (celebrityToDelete.image) await deleteImageFromStorage(celebrityToDelete.image);
      if (celebrityToDelete.mobile_image) await deleteImageFromStorage(celebrityToDelete.mobile_image);
      const { error } = await supabase.from('selebrity-slider').delete().eq('id', id);
      if (error) throw error;
      await loadCelebrities();
      setDeleteConfirm(null);
      setSuccess('Celebrity deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error deleting celebrity: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };
  
  const resetForm = () => {
    setEditingId(null);
    setFormData({
      fullname: '',
      russian_name: '',
      image: '',
      mobile_image: '',
      mobile_background_position: '50%',
      desktop_brightness: '100',
      mobile_brightness: '100'
    });
    setFileNames({ desktop: '', mobile: '' });
    setError('');
  };
  
  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCelebrities = celebrities.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(celebrities.length / itemsPerPage);
  const goToPage = (pageNumber) => setCurrentPage(pageNumber);
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  
  const getDisplayImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return getImageUrl(imagePath);
  };
  
  const handleImageError = (celebrityId, imageType) => {
    setImageErrors(prev => ({ ...prev, [`${celebrityId}-${imageType}`]: true }));
  };
  
  const getGlobalIndex = (pageIndex) => indexOfFirstItem + pageIndex;
  
  // Get preview image URL (fallback to desktop if mobile not available)
  const getPreviewImageUrl = () => {
    if (activeBrightnessType === 'desktop') {
      return getDisplayImageUrl(formData.image);
    } else {
      // For mobile preview, use mobile image if available, otherwise desktop
      return getDisplayImageUrl(formData.mobile_image) || getDisplayImageUrl(formData.image);
    }
  };
  
  // Convert position percentage to CSS value
  const getPositionFromPercent = (percent) => {
    const num = parseInt(percent);
    if (num <= 25) return 'left';
    if (num <= 45) return 'center left';
    if (num <= 55) return 'center';
    if (num <= 75) return 'center right';
    return 'right';
  };
  
  // Styles
  const styles = {
    container: { maxWidth: '100%', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif', color: '#ffffff' },
    header: { fontSize: '32px', fontWeight: '600', marginBottom: '30px', color: '#ffffff' },
    stats: { display: 'flex', gap: '10px', marginBottom: '20px' },
    statBadge: { backgroundColor: '#242527', color: '#c5c3c3', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '500' },
    addButtonHeader: { backgroundColor: '#3176FF', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px 24px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'background-color 0.2s', marginBottom: '20px' },
    formSection: { backgroundColor: '#1e1f22', borderRadius: '12px', padding: '30px', marginBottom: '40px', border: '1px solid #333' },
    formTitle: { fontSize: '20px', fontWeight: '600', marginBottom: '25px', color: '#ffffff' },
    formGroup: { marginBottom: '20px' },
    label: { display: 'block', marginBottom: '8px', fontSize: '14px', color: '#c4c4c4', fontWeight: '500' },
    input: { width: '100%', padding: '12px 15px', fontSize: '14px', border: 'none', borderBottom: '1px solid #5e5e5e', color: '#dfdede', backgroundColor: 'transparent', boxSizing: 'border-box', outline: 'none' },
    charCounter: { textAlign: 'right', fontSize: '13px', color: '#666', marginTop: '5px' },
    
    tabs: { display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #5e5e5e', paddingBottom: '10px' },
    tab: { padding: '8px 20px', borderRadius: '6px', border: 'none', background: 'transparent', color: '#c5c3c3', cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s' },
    activeTab: { background: '#3176FF', color: '#fff' },
    
    photoRowContainer: { display: 'flex', gap: '40px', alignItems: 'flex-start', width: '100%', flexWrap: 'wrap' },
    photoField: { flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '15px' },
    photoHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px', borderBottom: '1px solid #5e5e5e' },
    photoTitleContainer: { display: 'flex', flexDirection: 'column', gap: '4px', maxWidth: 'calc(100% - 40px)' },
    photoTitle: { fontSize: '14px', color: '#c5c3c3', fontWeight: '500' },
    fileName: { fontSize: '13px', color: '#8a8a8a', fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    addButton: { backgroundColor: '#c5c5c53a', color: '#fff', border: 'none', borderRadius: '60px', width: '30px', height: '30px', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    previewBox: { width: '150px', height: '150px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #696969', backgroundColor: '#2b2929', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    previewImg: { width: '100%', height: '100%', objectFit: 'cover' },
    previewPlaceholderSmall: { color: '#999', fontSize: '12px', textAlign: 'center', padding: '10px' },
    removeButton: { marginTop: '10px', padding: '6px 12px', backgroundColor: 'transparent', color: '#ff6b6b', border: '1px solid #ff6b6b', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', width: 'fit-content' },
    
    brightnessTypeToggle: { display: 'flex', gap: '10px', marginBottom: '20px' },
    toggleButton: { padding: '8px 16px', borderRadius: '6px', border: '1px solid #5e5e5e', background: 'transparent', color: '#c5c3c3', cursor: 'pointer' },
    activeToggleButton: { background: '#3176FF', borderColor: '#3176FF', color: '#fff' },
    
    positionSliderContainer: { marginTop: '20px' },
    positionSliderLabel: { display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px', color: '#c5c3c3' },
    positionSlider: { width: '100%', height: '6px', borderRadius: '3px', background: '#5e5e5e', WebkitAppearance: 'none' },
    positionPreviewBox: { width: '100%', maxWidth: '300px', height: '250px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #5e5e5e', marginTop: '20px', backgroundColor: '#2b2929' },
    positionPreviewImage: { width: '100%', height: '100%', objectFit: 'cover' },
    positionValue: { marginTop: '10px', fontSize: '12px', color: '#8a8a8a', textAlign: 'center' },
    
    brightnessSliderContainer: { marginTop: '20px' },
    brightnessSliderLabel: { display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px', color: '#c5c3c3' },
    brightnessSlider: { width: '100%', height: '6px', borderRadius: '3px', background: '#5e5e5e', WebkitAppearance: 'none' },
    brightnessPreviewBox: { width: '100%', maxWidth: '300px', height: '250px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #5e5e5e', marginTop: '20px', backgroundColor: '#2b2929' },
    brightnessPreviewImage: { width: '100%', height: '100%', objectFit: 'cover' },
    brightnessValue: { marginTop: '10px', fontSize: '12px', color: '#8a8a8a', textAlign: 'center' },
    
    formActions: { display: 'flex', gap: '20px', marginTop: '30px' },
    saveButton: { backgroundColor: '#3176FF', color: '#fff', border: 'none', borderRadius: '8px', padding: '14px 32px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    resetButton: { backgroundColor: 'transparent', color: '#ffffff', border: '1px solid #5e5e5e', borderRadius: '8px', padding: '14px 32px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    
    projectsTable: { width: '100%', backgroundColor: '#161719', overflow: 'auto' },
    tableHeader: { display: 'grid', gridTemplateColumns: '80px 80px 100px 1.5fr 1fr 120px', gap: '20px', padding: '15px 0', borderBottom: '1px solid #333', marginBottom: '10px', alignItems: 'center', minWidth: '700px' },
    tableHeaderCell: { fontWeight: '600', fontSize: '14px', color: '#c5c3c3', textTransform: 'uppercase' },
    celebrityRow: { display: 'grid', gridTemplateColumns: '80px 80px 100px 1.5fr 1fr 120px', gap: '20px', padding: '15px 0', alignItems: 'center', borderBottom: '1px solid #333', backgroundColor: '#161719', minWidth: '700px' },
    orderCell: { display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' },
    orderButton: { width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent', border: '1px solid #5e5e5e', borderRadius: '4px', color: '#ffffff', cursor: 'pointer' },
    orderButtonDisabled: { opacity: 0.3, cursor: 'not-allowed' },
    idCell: { fontSize: '14px', color: '#c5c3c3' },
    imageContainer: { width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #696969', backgroundColor: '#2b2929', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    celebrityImage: { width: '100%', height: '100%', objectFit: 'cover' },
    imagePlaceholder: { color: '#999', fontSize: '10px', textAlign: 'center', padding: '5px' },
    nameCell: { fontSize: '14px', fontWeight: '600', color: '#ffffff' },
    russianNameCell: { fontSize: '13px', color: '#c5c3c3' },
    dateCell: { fontSize: '14px', color: '#c5c3c3' },
    buttonsCell: { display: 'flex', gap: '10px' },
    actionButton: { padding: '8px', fontSize: '14px', borderRadius: '6px', cursor: 'pointer', border: 'none', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent' },
    editButton: { border: '1px solid #5e5e5e' },
    deleteButton: { border: '1px solid #ff6b6b' },
    pagination: { display: 'flex', justifyContent: 'flex-start', gap: '8px', marginTop: '30px' },
    pageNumberButton: { padding: '6px 12px', backgroundColor: 'transparent', color: '#c5c3c3', border: '1px solid transparent', borderRadius: '6px', cursor: 'pointer', minWidth: '36px' },
    currentPageButton: { backgroundColor: '#242527', border: '1px solid #5e5e5e' },
    deleteModal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: '#242527', padding: '30px', borderRadius: '12px', maxWidth: '500px', width: '100%', border: '1px solid #5e5e5e' },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#ffffff', marginBottom: '15px' },
    modalText: { fontSize: '16px', color: '#c5c3c3', marginBottom: '25px', lineHeight: '1.5' },
    modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '15px' },
    cancelButton: { padding: '12px 24px', backgroundColor: 'transparent', color: '#c5c3c3', border: '1px solid #5e5e5e', borderRadius: '8px', cursor: 'pointer' },
    confirmDeleteButton: { padding: '12px 24px', backgroundColor: 'rgba(255,107,107,0.1)', color: '#ff6b6b', border: '1px solid #ff6b6b', borderRadius: '8px', cursor: 'pointer' },
    errorMessage: { backgroundColor: 'rgba(255,0,0,0.1)', color: '#ff6b6b', padding: '15px', borderRadius: '8px', marginBottom: '20px' },
    successMessage: { backgroundColor: 'rgba(0,255,0,0.1)', color: '#51cf66', padding: '15px', borderRadius: '8px', marginBottom: '20px' },
    loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '200px', gap: '20px' },
    spinner: { width: '30px', height: '30px', border: '3px solid rgba(255,255,255,0.1)', borderTop: '3px solid #3176FF', borderRadius: '50%', animation: 'spin 1s linear infinite' },
    smallSpinner: { width: '16px', height: '16px', border: '2px solid #f3f3f3', borderTop: '2px solid #3176FF', borderRadius: '50%', animation: 'spin 1s linear infinite' },
    emptyState: { textAlign: 'center', padding: '60px 20px', backgroundColor: '#242527', borderRadius: '12px', border: '1px dashed #5e5e5e' },
    emptyStateIcon: { fontSize: '48px', marginBottom: '20px' },
    emptyStateTitle: { fontSize: '24px', fontWeight: '600', marginBottom: '10px', color: '#ffffff' },
    emptyStateText: { color: '#c5c3c3' }
  };
  
  if (loading && celebrities.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <div>Loading celebrities...</div>
        </div>
      </div>
    );
  }
  
  const previewImageUrl = getPreviewImageUrl();
  const currentBrightness = activeBrightnessType === 'desktop' 
    ? parseInt(formData.desktop_brightness) / 100 
    : parseInt(formData.mobile_brightness) / 100;
  
  return (
    <div style={styles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }}>
        <h1 style={styles.header}>Selebrity Management</h1>
        <div style={styles.stats}>
          <span style={styles.statBadge}>Total: {celebrities.length}</span>
          <button onClick={() => { resetForm(); setShowForm(!showForm); }} style={styles.addButtonHeader}>
            {showForm ? '− Cancel' : '+ Add New'}
          </button>
        </div>
      </div>
      
      {error && <div style={styles.errorMessage}>{error}</div>}
      {success && <div style={styles.successMessage}>{success}</div>}
      
      {showForm && (
        <div style={styles.formSection}>
          <h2 style={styles.formTitle}>{editingId ? 'Edit Celebrity' : 'Add New Celebrity'}</h2>
          
          {/* Tabs */}
          <div style={styles.tabs}>
            <button onClick={() => setActiveTab('basic')} style={{...styles.tab, ...(activeTab === 'basic' ? styles.activeTab : {})}}>Basic Info</button>
            <button onClick={() => setActiveTab('position')} style={{...styles.tab, ...(activeTab === 'position' ? styles.activeTab : {})}}>Mobile Position</button>
            <button onClick={() => setActiveTab('brightness')} style={{...styles.tab, ...(activeTab === 'brightness' ? styles.activeTab : {})}}>Image Brightness</button>
          </div>
          
          {/* Tab 1: Basic Info */}
          {activeTab === 'basic' && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                <div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>ENGLISH NAME *</label>
                    <input type="text" value={formData.fullname} onChange={(e) => setFormData({...formData, fullname: e.target.value.toUpperCase()})} placeholder="e.g., MATTHEW MCCONAUGHEY" style={styles.input} maxLength="100" />
                    <div style={styles.charCounter}>{formData.fullname.length} / 100</div>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>RUSSIAN NAME (optional)</label>
                    <input type="text" value={formData.russian_name} onChange={(e) => setFormData({...formData, russian_name: e.target.value})} placeholder="e.g., Matthew McConaughey" style={styles.input} maxLength="100" />
                    <div style={styles.charCounter}>{formData.russian_name.length} / 100</div>
                  </div>
                </div>
                
                <div>
                  <div style={styles.photoRowContainer}>
                    <div style={styles.photoField}>
                      <div style={styles.photoHeader}>
                        <div style={styles.photoTitleContainer}>
                          <div style={styles.photoTitle}>DESKTOP IMAGE *</div>
                          {fileNames.desktop && <div style={styles.fileName}>{fileNames.desktop}</div>}
                        </div>
                        <input ref={desktopFileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif" onChange={handleDesktopImageUpload} style={{ display: 'none' }} id="desktop-upload" />
                        <label htmlFor="desktop-upload" style={styles.addButton}>{uploadingDesktop ? <div style={styles.smallSpinner}></div> : (formData.image ? '✓' : '+')}</label>
                      </div>
                      <div style={styles.previewBox}>
                        {formData.image ? <img src={getDisplayImageUrl(formData.image)} alt="Desktop preview" style={styles.previewImg} /> : <div style={styles.previewPlaceholderSmall}>No image</div>}
                      </div>
                      {formData.image && <button onClick={() => handleRemoveImage('desktop')} style={styles.removeButton}>Remove</button>}
                    </div>
                    
                    <div style={styles.photoField}>
                      <div style={styles.photoHeader}>
                        <div style={styles.photoTitleContainer}>
                          <div style={styles.photoTitle}>MOBILE IMAGE</div>
                          {fileNames.mobile && <div style={styles.fileName}>{fileNames.mobile}</div>}
                        </div>
                        <input ref={mobileFileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif" onChange={handleMobileImageUpload} style={{ display: 'none' }} id="mobile-upload" />
                        <label htmlFor="mobile-upload" style={styles.addButton}>{uploadingMobile ? <div style={styles.smallSpinner}></div> : (formData.mobile_image ? '✓' : '+')}</label>
                      </div>
                      <div style={styles.previewBox}>
                        {formData.mobile_image ? <img src={getDisplayImageUrl(formData.mobile_image)} alt="Mobile preview" style={styles.previewImg} /> : <div style={styles.previewPlaceholderSmall}>No image</div>}
                      </div>
                      {formData.mobile_image && <button onClick={() => handleRemoveImage('mobile')} style={styles.removeButton}>Remove</button>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Tab 2: Mobile Position - Slider control */}
          {activeTab === 'position' && (
            <div>
              <label style={styles.label}>MOBILE BACKGROUND POSITION</label>
              <div style={styles.positionSliderContainer}>
                <div style={styles.positionSliderLabel}>
                  <span>Left (0%)</span>
                  <span>Center (50%)</span>
                  <span>Right (100%)</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={parseInt(formData.mobile_background_position) || 50}
                  onChange={(e) => setFormData({...formData, mobile_background_position: e.target.value + '%'})}
                  style={styles.positionSlider}
                />
                <div style={styles.positionValue}>
                  Current position: <strong>{formData.mobile_background_position}</strong> 
                  ({getPositionFromPercent(formData.mobile_background_position)})
                </div>
              </div>
              
              <div style={{ fontSize: '12px', color: '#8a8a8a', marginTop: '8px' }}>
                Move the slider left or right to adjust image position on mobile devices
              </div>
              
              {/* Preview uses mobile image if available, otherwise desktop */}
              {(formData.mobile_image || formData.image) && (
                <div style={styles.positionPreviewBox}>
                  <img 
                    src={getDisplayImageUrl(formData.mobile_image) || getDisplayImageUrl(formData.image)} 
                    alt="Position preview" 
                    style={{
                      ...styles.positionPreviewImage,
                      objectPosition: formData.mobile_background_position
                    }}
                  />
                </div>
              )}
              
              <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#2b2b2e', borderRadius: '8px' }}>
                <div style={{ fontSize: '13px', color: '#c5c3c3', marginBottom: '10px' }}>Tips:</div>
                <div style={{ fontSize: '12px', color: '#8a8a8a' }}>
                  • 0% = Image aligned to the left<br />
                  • 50% = Image centered (default)<br />
                  • 100% = Image aligned to the right<br />
                  • This only affects mobile devices (screens ≤ 768px)
                </div>
              </div>
            </div>
          )}
          
          {/* Tab 3: Image Brightness */}
          {activeTab === 'brightness' && (
            <div>
              <div style={styles.brightnessTypeToggle}>
                <button onClick={() => setActiveBrightnessType('desktop')} style={{...styles.toggleButton, ...(activeBrightnessType === 'desktop' ? styles.activeToggleButton : {})}}>Desktop Brightness</button>
                <button onClick={() => setActiveBrightnessType('mobile')} style={{...styles.toggleButton, ...(activeBrightnessType === 'mobile' ? styles.activeToggleButton : {})}}>Mobile Brightness</button>
              </div>
              
              <div style={styles.brightnessSliderContainer}>
                <div style={styles.brightnessSliderLabel}>
                  <span>Dark (0%)</span>
                  <span>Normal (100%)</span>
                  <span>Bright (200%)</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="200"
                  step="1"
                  value={activeBrightnessType === 'desktop' ? parseInt(formData.desktop_brightness) : parseInt(formData.mobile_brightness)}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (activeBrightnessType === 'desktop') {
                      setFormData(prev => ({ ...prev, desktop_brightness: value }));
                    } else {
                      setFormData(prev => ({ ...prev, mobile_brightness: value }));
                    }
                  }}
                  style={styles.brightnessSlider}
                />
                <div style={styles.brightnessValue}>
                  Current brightness: <strong>{activeBrightnessType === 'desktop' ? formData.desktop_brightness : formData.mobile_brightness}%</strong>
                  {parseInt(activeBrightnessType === 'desktop' ? formData.desktop_brightness : formData.mobile_brightness) < 100 && ' (Darker)'}
                  {parseInt(activeBrightnessType === 'desktop' ? formData.desktop_brightness : formData.mobile_brightness) > 100 && ' (Brighter)'}
                  {parseInt(activeBrightnessType === 'desktop' ? formData.desktop_brightness : formData.mobile_brightness) === 100 && ' (Normal)'}
                </div>
              </div>
              
              <div style={{ fontSize: '12px', color: '#8a8a8a', marginTop: '8px' }}>
                Move the slider to adjust image brightness<br />
                • 0% = Completely dark (black)<br />
                • 100% = Original brightness (default)<br />
                • 200% = Maximum brightness
              </div>
              
              {/* Live preview with brightness - uses mobile image if available for mobile preview, otherwise desktop */}
              {previewImageUrl && (
                <div style={styles.brightnessPreviewBox}>
                  <img 
                    src={previewImageUrl} 
                    alt="Brightness preview" 
                    style={{
                      ...styles.brightnessPreviewImage,
                      filter: `brightness(${currentBrightness})`
                    }}
                  />
                </div>
              )}
              
              <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#2b2b2e', borderRadius: '8px' }}>
                <div style={{ fontSize: '13px', color: '#c5c3c3', marginBottom: '10px' }}>About Brightness:</div>
                <div style={{ fontSize: '12px', color: '#8a8a8a' }}>
                  • Use lower values (30-70%) to darken the image for better text readability<br />
                  • Use higher values (120-150%) to brighten dark photos<br />
                  • 100% keeps the original image brightness<br />
                  • Desktop and mobile can have different brightness settings<br />
                  • If no mobile image is uploaded, desktop image is used for preview
                </div>
              </div>
            </div>
          )}
          
          <div style={styles.formActions}>
            <button onClick={handleSave} style={styles.saveButton} disabled={loading || uploadingDesktop || uploadingMobile}>
              {loading ? 'Saving...' : editingId ? 'Update' : 'Save'}
            </button>
            <button onClick={() => { resetForm(); setShowForm(false); }} style={styles.resetButton}>Cancel</button>
          </div>
        </div>
      )}
      
      {celebrities.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyStateIcon}>⭐</div>
          <h3 style={styles.emptyStateTitle}>No celebrities yet</h3>
          <p style={styles.emptyStateText}>Click "Add New" to create your first entry</p>
        </div>
      ) : (
        <>
          <div style={styles.projectsTable}>
            <div style={styles.tableHeader}>
              <div style={styles.tableHeaderCell}>ORDER</div>
              <div style={styles.tableHeaderCell}>ID</div>
              <div style={styles.tableHeaderCell}>PHOTO</div>
              <div style={styles.tableHeaderCell}>NAME</div>
              <div style={styles.tableHeaderCell}>RUSSIAN NAME</div>
              <div style={styles.tableHeaderCell}>ACTIONS</div>
            </div>
            
            {currentCelebrities.map((celebrity, pageIndex) => {
              const globalIndex = getGlobalIndex(pageIndex);
              const desktopImageUrl = getDisplayImageUrl(celebrity.image);
              const desktopHasError = imageErrors[`${celebrity.id}-desktop`];
              
              return (
                <div key={celebrity.id} style={styles.celebrityRow}>
                  <div style={styles.orderCell}>
                    <button onClick={() => moveCelebrityUp(globalIndex)} style={{...styles.orderButton, ...((globalIndex === 0 || updatingOrder) ? styles.orderButtonDisabled : {})}} disabled={globalIndex === 0 || updatingOrder}>▲</button>
                    <button onClick={() => moveCelebrityDown(globalIndex)} style={{...styles.orderButton, ...((globalIndex === celebrities.length - 1 || updatingOrder) ? styles.orderButtonDisabled : {})}} disabled={globalIndex === celebrities.length - 1 || updatingOrder}>▼</button>
                  </div>
                  <div style={styles.idCell}>#{celebrity.id}</div>
                  <div>
                    <div style={styles.imageContainer}>
                      {desktopImageUrl && !desktopHasError ? (
                        <img 
                          src={desktopImageUrl} 
                          alt={celebrity.fullname} 
                          style={{
                            ...styles.celebrityImage,
                            filter: `brightness(${parseInt(celebrity.desktop_brightness || '100') / 100})`
                          }} 
                          onError={() => handleImageError(celebrity.id, 'desktop')} 
                        />
                      ) : (
                        <div style={styles.imagePlaceholder}>No image</div>
                      )}
                    </div>
                  </div>
                  <div>
                    <div style={styles.nameCell}>{celebrity.fullname}</div>
                    {celebrity.mobile_background_position && celebrity.mobile_background_position !== '50%' && (
                      <div style={{ fontSize: '10px', color: '#888', marginTop: '4px' }}>Position: {celebrity.mobile_background_position}</div>
                    )}
                    {celebrity.desktop_brightness && celebrity.desktop_brightness !== '100' && (
                      <div style={{ fontSize: '10px', color: '#888', marginTop: '2px' }}>Brightness: {celebrity.desktop_brightness}%</div>
                    )}
                  </div>
                  <div style={styles.russianNameCell}>{celebrity.russian_name || '—'}</div>
                  <div style={styles.buttonsCell}>
                    <button onClick={() => handleEdit(celebrity)} style={{...styles.actionButton, ...styles.editButton}} disabled={updatingOrder}>✏️</button>
                    <button onClick={() => setDeleteConfirm(celebrity.id)} style={{...styles.actionButton, ...styles.deleteButton}} disabled={updatingOrder}>🗑️</button>
                  </div>
                </div>
              );
            })}
          </div>
          
          {totalPages > 1 && (
            <div style={styles.pagination}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
                <button key={pageNumber} onClick={() => goToPage(pageNumber)} style={{...styles.pageNumberButton, ...(pageNumber === currentPage ? styles.currentPageButton : {})}}>
                  {pageNumber}
                </button>
              ))}
            </div>
          )}
        </>
      )}
      
      {deleteConfirm && (
        <div style={styles.deleteModal}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>Confirm Deletion</h3>
            <p style={styles.modalText}>
              Are you sure you want to delete this celebrity?<br /><br />
              <strong>This will also delete all associated images from storage.</strong><br />
              This action cannot be undone.
            </p>
            <div style={styles.modalActions}>
              <button onClick={() => setDeleteConfirm(null)} style={styles.cancelButton} disabled={deleting || updatingOrder}>Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} style={styles.confirmDeleteButton} disabled={deleting || updatingOrder}>{deleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        button:disabled { opacity: 0.5; cursor: not-allowed; }
        input:focus, select:focus { border-color: #3176FF !important; outline: none; }
        select option { background-color: #1e1f22; color: #dfdede; }
        input[type="range"] { -webkit-appearance: none; background: #5e5e5e; height: 6px; border-radius: 3px; }
        input[type="range"]::-webkit-slider-thumb { -webkit-appearance: none; width: 18px; height: 18px; border-radius: 50%; background: #3176FF; cursor: pointer; border: none; }
        input[type="range"]::-webkit-slider-thumb:hover { background: #2559cc; transform: scale(1.2); }
      `}</style>
    </div>
  );
};

export default SelebrityManager;